import { useQuery } from 'hooks/sympl-query';
import { GET_VACANCY_COPY } from 'graphql/vacancies/queries';
import useNavigationContext from 'hooks/context/nav-context';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import {
  UpdateVacancyCopy,
  VacancyContextType,
  VacancyCopy,
} from 'types/vacancyTypes';
import { useMutation } from 'hooks/sympl-mutation';
import {
  UPDATE_VACANCY_COPY,
  UPDATE_VACANCY_CUSTOM_URL,
} from 'graphql/vacancies/mutations';
import { getRandomText } from 'utils/wordpressHelpers';
import { VacancyPromptPayload } from 'utils/vacancy-generator/vacancyHelpers';

export const VacancyContext = createContext<VacancyContextType>(undefined!);

export const VacancyProvider: React.FC<React.ReactNode> = ({ children }) => {
  const { activeVacancy, setHasUnsavedChanges } = useNavigationContext();

  const [copyData, _setCopyData] = useState<VacancyCopy>();
  const [payload, setPayload] = useState<VacancyPromptPayload>();
  const [generatingFields, setGeneratingFields] = useState<
    (keyof VacancyCopy)[]
  >([]);

  const {
    loading: copyLoading,
    data,
    refetch: refetchCopy,
  } = useQuery<
    {
      vacCopy?: VacancyCopy;
    },
    { vacancyId: number }
  >(GET_VACANCY_COPY, {
    skip: !activeVacancy,
    variables: { vacancyId: activeVacancy ?? 0 },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      _setCopyData(data?.vacCopy);
    },
  });

  const setCopyData = (data: React.SetStateAction<VacancyCopy | undefined>) => {
    _setCopyData(data);
    setHasUnsavedChanges(true);
  };

  const [updateCopy] = useMutation<UpdateVacancyCopy>(UPDATE_VACANCY_COPY);
  const [updateCustomUrl] = useMutation<UpdateVacancyCopy>(
    UPDATE_VACANCY_CUSTOM_URL
  );

  const saveHandler = async (vacancyDataCopy?: VacancyCopy): Promise<void> => {
    const vacancyCopyForSave: Partial<VacancyCopy> = { ...vacancyDataCopy };
    delete vacancyCopyForSave.salary_metadata;

    const tempvacancyData = {
      ...vacancyCopyForSave,
      vac_text_uniqueness: JSON.stringify(vacancyDataCopy?.vac_text_uniqueness),
      vac_text_uniqueness_beta: vacancyDataCopy?.vac_text_uniqueness,
      pictures: {
        logo: vacancyDataCopy?.pictures?.logo?.id,
        image_cb1: vacancyDataCopy?.pictures?.image_cb1?.id,
        image_cb3: vacancyDataCopy?.pictures?.image_cb3?.id,
      },
    };

    await updateCopy({
      variables: {
        vacancyId: activeVacancy,
        input: tempvacancyData,
      },
    });
  };

  const saveCustomUrlHandler = async (
    customUrl: string,
    cb?: () => void
  ): Promise<void> => {
    if (activeVacancy?.constructor !== Number) return;
    try {
      const variables = {
        vacancyId: activeVacancy,
        input: {
          vacancy_url: customUrl,
        },
      };

      await updateCustomUrl({
        variables,
      });

      // The URL has already been persisted by its dedicated endpoint. Keep the
      // rest of the local copy intact so pending vacancy-page edits stay pending.
      _setCopyData((currentCopy) =>
        currentCopy ? { ...currentCopy, vac_url: customUrl } : currentCopy
      );

      cb?.();
    } catch (e) {}
  };

  useEffect(() => {
    // Set vacancy data if it hasn't been set or if the vacancy has changed
    if (
      !copyData ||
      (data && Object.values(data).length && copyData.id !== data.vacCopy?.id)
    ) {
      _setCopyData(data?.vacCopy);
      return;
    }

    // PROBLEM: return () => setHasUnsavedChanges(false);
  }, [data, setHasUnsavedChanges, copyData]);

  const dummyTxt = useMemo(() => getRandomText(35), []);

  return (
    <VacancyContext.Provider
      value={{
        copyData,
        setCopyData,
        saveHandler,
        saveCustomUrlHandler,
        dummyTxt,
        copyLoading,
        refetchCopy,
        generatingFields,
        setGeneratingFields,
        payload,
        setPayload,
      }}
    >
      {children}
    </VacancyContext.Provider>
  );
};
